// =============================================================================
// App Shell - Sidebar a gruppi (desktop) + drawer mobile
// =============================================================================

import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Settings,
  LogOut,
  User,
  Users,
  LayoutDashboard,
  BarChart3,
  Inbox,
  Menu,
  X,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useTenantStore } from '../store/tenantStore';
import { TenantSwitcher } from './TenantSwitcher';
import { LanguageSelector } from './common/LanguageSelector';
import { useBookingRequestsPendingCount } from '../api/hooks';

// -----------------------------------------------------------------------------
// Definizione voci di navigazione, raggruppate "a cascata": prima l'operatività
// quotidiana (con il Calendario come hub in cima), poi l'analisi, infine il
// sistema. I gruppi admin-only vengono filtrati in base al ruolo.
// -----------------------------------------------------------------------------

interface NavItem {
  to: string;
  icon: React.ElementType;
  labelKey: string;
  badge?: number;
}

interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

function useNavGroups(): NavGroup[] {
  const activeRole = useTenantStore((s) => s.activeRole);
  const isAdmin = activeRole === 'ADMIN' || activeRole === 'OWNER';
  const { data: pendingCount = 0 } = useBookingRequestsPendingCount();

  const operations: NavItem[] = [
    { to: '/scheduler', icon: Calendar, labelKey: 'nav.calendar' },
    { to: '/customers', icon: Users, labelKey: 'nav.customers' },
  ];
  if (isAdmin) {
    operations.push({
      to: '/booking-requests',
      icon: Inbox,
      labelKey: 'nav.bookingRequests',
      badge: pendingCount,
    });
  }

  const groups: NavGroup[] = [
    { labelKey: 'nav.groups.operations', items: operations },
    {
      labelKey: 'nav.groups.insights',
      items: [
        { to: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
        { to: '/reports', icon: BarChart3, labelKey: 'nav.reports' },
      ],
    },
  ];

  if (isAdmin) {
    groups.push({
      labelKey: 'nav.groups.system',
      items: [{ to: '/settings', icon: Settings, labelKey: 'nav.settings' }],
    });
  }

  return groups;
}

// -----------------------------------------------------------------------------
// Contenuto condiviso della sidebar (usato sia dalla colonna desktop sia dal
// drawer mobile). `onNavigate` chiude il drawer quando si clicca una voce.
// -----------------------------------------------------------------------------

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation('common');
  const { user, logout } = useAuthStore();
  const activeRole = useTenantStore((s) => s.activeRole);
  const clearTenant = useTenantStore((s) => s.clearTenant);
  const groups = useNavGroups();
  const isProfileActive = useLocation().pathname === '/profile';

  const handleLogout = () => {
    logout();
    clearTenant();
    localStorage.removeItem('skedulab-auth');
    localStorage.removeItem('skedulab-tenant');
    window.location.href = '/login';
  };

  return (
    <div className="flex h-full flex-col">
      {/* Logo → home (calendario) */}
      <div className="px-4 pt-4 pb-3">
        <Link
          to="/scheduler"
          onClick={onNavigate}
          className="flex items-center gap-2"
        >
          <img src="/logo.svg" alt="Skedulab" className="h-8 w-auto" />
          <span className="text-lg font-semibold text-gray-900">SKEDULAB</span>
        </Link>
      </div>

      {/* Selettore organizzazione */}
      <div className="px-4 pb-3">
        <TenantSwitcher />
      </div>

      <div className="mx-4 h-px bg-gray-100" />

      {/* Gruppi di navigazione */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-5">
        {groups.map((group) => (
          <div key={group.labelKey}>
            <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              {t(group.labelKey)}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <SidebarLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={t(item.labelKey)}
                  badge={item.badge}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer: utente + lingua + logout */}
      <div className="border-t border-gray-100 px-4 py-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          {/* Il blocco utente è la porta d'ingresso all'account: nome, email e
              password si cambiano da lì. */}
          <Link
            to="/profile"
            onClick={onNavigate}
            title={t('nav.profile')}
            className={`flex items-center gap-2 min-w-0 text-sm text-gray-600 rounded-lg -m-1 p-1 transition-colors hover:bg-gray-50 ${
              isProfileActive ? 'bg-gray-50' : ''
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                isProfileActive ? 'bg-emerald-50' : 'bg-gray-100'
              }`}
            >
              <User className={`w-4 h-4 ${isProfileActive ? 'text-emerald-600' : 'text-gray-500'}`} />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-400">{activeRole}</p>
            </div>
          </Link>
          <LanguageSelector />
        </div>

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title={t('nav.logout')}
        >
          <LogOut className="w-4 h-4" />
          <span>{t('nav.logout')}</span>
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Singola voce di navigazione
// -----------------------------------------------------------------------------

interface SidebarLinkProps {
  to: string;
  icon: React.ElementType;
  label: string;
  badge?: number;
  onNavigate?: () => void;
}

function SidebarLink({ to, icon: Icon, label, badge, onNavigate }: SidebarLinkProps) {
  const location = useLocation();
  const isActive =
    location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={`relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? 'bg-emerald-50 text-emerald-600'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      {/* Indicatore attivo a sinistra */}
      <span
        className={`absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-emerald-500 transition-opacity ${
          isActive ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-red-500 text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

// -----------------------------------------------------------------------------
// Shell principale: sidebar desktop + top bar/drawer mobile + area contenuto
// -----------------------------------------------------------------------------

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Chiudi il drawer al cambio rotta e col tasto Escape
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  return (
    <div className="flex h-full w-full">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:flex-col w-64 flex-shrink-0 bg-white border-r border-gray-200">
        <SidebarContent />
      </aside>

      {/* Drawer mobile */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-gray-900/40 animate-in fade-in duration-150"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          {/* Pannello */}
          <div className="relative w-72 max-w-[80%] bg-white shadow-xl animate-in slide-in-from-left duration-200">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 z-10 p-2 rounded-lg text-gray-500 hover:bg-gray-100"
              title={t('nav.closeMenu')}
              aria-label={t('nav.closeMenu')}
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Colonna contenuto */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar mobile */}
        <header className="md:hidden flex items-center gap-3 bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-lg text-gray-600 hover:bg-gray-100"
            title={t('nav.menu')}
            aria-label={t('nav.menu')}
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/scheduler" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Skedulab" className="h-7 w-auto" />
            <span className="text-base font-semibold text-gray-900">SKEDULAB</span>
          </Link>
        </header>

        <div className="flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

export default AppShell;
